import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendOtpApi } from "../model/authApi";
import { routes } from "@/app/navigation/routes";
import { PHONE_DIAL_CODE_OPTIONS, type PhoneDialCode } from "../constants/phoneDialCodes";
import { SESSION_PHONE_MOBILE_INPUT_PREFILL } from "../constants/sessionStorageKeys";

export const usePhoneMobileInputViewModel = () => {
  const defaultDial = PHONE_DIAL_CODE_OPTIONS[0]?.dialCode ?? ("+1" as PhoneDialCode);

  const [dialCode, setDialCodeState] = useState<PhoneDialCode>(defaultDial);
  const [nationalNumber, setNationalNumber] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const selectedDialMeta = useMemo(
    () =>
      PHONE_DIAL_CODE_OPTIONS.find((o) => o.dialCode === dialCode) ?? PHONE_DIAL_CODE_OPTIONS[0],
    [dialCode]
  );

  const fullPhone = useMemo(() => `${dialCode}${nationalNumber}`, [dialCode, nationalNumber]);

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_PHONE_MOBILE_INPUT_PREFILL);
    sessionStorage.removeItem(SESSION_PHONE_MOBILE_INPUT_PREFILL);

    if (!stored?.trim().startsWith("+")) {
      return;
    }
    const match = PHONE_DIAL_CODE_OPTIONS.find((o) => stored.startsWith(o.dialCode));
    if (match) {
      const national = stored.slice(match.dialCode.length).replace(/\D/g, "");
      if (national) {
        setDialCodeState(match.dialCode);
        setNationalNumber(national);
      }
    }
  }, []);

  const setDialCode = useCallback((value: PhoneDialCode) => {
    setDialCodeState(value);
    setError("");
  }, []);

  const handleNationalNumberChange = useCallback(
    (value: string) => {
      const maxLen = selectedDialMeta?.nationalDigitsLength ?? 10;
      const digits = value.replace(/\D/g, "").slice(0, maxLen);
      setNationalNumber(digits);
      setError("");
    },
    [selectedDialMeta?.nationalDigitsLength]
  );

  const validateMobile = useCallback(() => {
    const maxLen = selectedDialMeta?.nationalDigitsLength ?? 10;

    if (!nationalNumber) {
      return "Mobile number is required";
    }

    if (nationalNumber.length !== maxLen) {
      return `Enter exactly ${maxLen} digits`;
    }

    const combined = `${dialCode}${nationalNumber}`;
    if (!/^\+[0-9]+$/.test(combined)) {
      return "Invalid phone number";
    }

    return "";
  }, [dialCode, nationalNumber, selectedDialMeta?.nationalDigitsLength]);

  const handleSubmit = useCallback(async () => {
    const validationError = validateMobile();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setError("");
      const phone = fullPhone;
      await sendOtpApi({ phone });
      sessionStorage.setItem("phone", phone);
      navigate(routes.phoneVerification);
    } catch {
      setError("Failed to send OTP. Please try again.");
    }
  }, [fullPhone, navigate, validateMobile]);

  return {
    dialCode,
    setDialCode,
    nationalNumber,
    setNationalNumber: handleNationalNumberChange,
    dialCodeOptions: PHONE_DIAL_CODE_OPTIONS,
    handleSubmit,
    error,
  };
};
